import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { LegalConsentService } from '../services/legal-consent.service';

@Component({
  selector: 'app-privacy-policy-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>
      <section class="content">
        <div class="content-inner">
          <h1>Privacy Policy – United Team Building</h1>
          <p class="last-updated">Last Updated: 4/8/2026</p>

          <h2>1. Information We Collect</h2>
          <p>We collect information that you voluntarily provide, including but not limited to:</p>
          <ul>
            <li>Name</li>
            <li>Phone number</li>
            <li>Email address</li>
            <li>Account information</li>
            <li>Media content (such as photos or videos) that you choose to upload</li>
          </ul>
          <p>United Team Building collects and processes only the data necessary to operate event management and media workflows.</p>

          <h2>2. How We Use Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide account and service notifications.</li>
            <li>Send transactional SMS messages you have opted into.</li>
            <li>Operate, maintain, and improve our services.</li>
            <li>Display user-submitted media in accordance with your consent.</li>
            <li>Comply with legal, regulatory, and carrier requirements.</li>
          </ul>
          <p>Audit and operational logs may be retained for security and troubleshooting.</p>
          <p>We do not sell or rent your personal information.</p>
          <p>User profile, role, and organization data are used to enforce access permissions.</p>

          <h2>3. SMS Communications</h2>
          <p>If you opt in to receive SMS notifications:</p>
          <ul>
            <li>Messages are transactional and informational only.</li>
            <li>Message frequency varies based on account activity.</li>
            <li>Message and data rates may apply.</li>
            <li>You may opt out at any time by replying STOP.</li>
            <li>For assistance, reply HELP.</li>
          </ul>
          <p>We rely on Azure Communication Services and mobile carriers to automatically enforce opt-out requests.</p>

          <h2>4. Media Consent and User-Submitted Content</h2>
          <p>If you upload photos, videos, or other media to United Team Building:</p>
          <ul>
            <li>You retain ownership of your content.</li>
            <li>You grant United Team Building a nonexclusive, perpetual, worldwide, royalty-free license to use, display, reproduce, and distribute the content for platform functionality, promotion, and service-related purposes.</li>
            <li>You represent that you have the legal right to upload the content and that it does not infringe on the rights of others.</li>
          </ul>
          <p>Uploaded media and related metadata are stored to support event operations.</p>
          <p>We may remove content at our discretion for moderation, safety, or compliance reasons.</p>

          <h2>5. Data Retention</h2>
          <p>We retain personal data only for as long as necessary to provide services, comply with legal obligations, resolve disputes, and enforce agreements.</p>

          <h2>6. Data Security</h2>
          <p>We implement reasonable technical and organizational safeguards to protect your information; however, no system can be guaranteed to be 100% secure.</p>

          <h2>7. Your Choices</h2>
          <p>You may:</p>
          <ul>
            <li>Update your communication preferences in your account.</li>
            <li>Opt out of SMS messages at any time.</li>
            <li>Request removal of content you have submitted (subject to operational or legal needs).</li>
          </ul>

          <h2>8. Contact Us</h2>
          <p>If you have questions about this Privacy Policy:</p>
          <p>United Team Building.</p>
          <p>Email: <a href="mailto:support@unitedteambuilding.com">support@unitedteambuilding.com</a></p>

          <p class="muted">Accept the privacy policy to continue using protected platform features.</p>

          @if (message()) {
            <div class="status">{{ message() }}</div>
          }

          @if (isLoggedIn()) {
            <button class="btn" [disabled]="busy()" (click)="acceptPrivacy()">I Accept Privacy Policy</button>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`
    .content { padding: 2rem 1.25rem 4rem; }
    .content-inner { max-width: 920px; margin: 0 auto; border: 1px solid var(--color-border); border-radius: .9rem; padding: 1.1rem 1.2rem; background: color-mix(in srgb, var(--color-panel) 80%, transparent); }
    h1 { margin-bottom: .4rem; }
    .last-updated { margin-top: 0; color: var(--color-muted); }
    h2 { margin-top: 1.1rem; margin-bottom: .35rem; font-size: 1.03rem; }
    p, li { line-height: 1.45; }
    ul { margin-top: .25rem; margin-bottom: .55rem; }
    a { color: var(--color-accent-soft); }
    .muted { color: var(--color-muted); }
    .status { margin: .8rem 0; padding: .65rem .75rem; border-radius: .6rem; border: 1px solid color-mix(in srgb, var(--color-accent-soft) 45%, transparent); background: color-mix(in srgb, var(--color-accent-soft) 12%, transparent); }
    .btn { margin-top: .75rem; border: 1px solid var(--color-accent); background: var(--color-accent); color: #fff; border-radius: .55rem; padding: .55rem .95rem; font-weight: 600; cursor: pointer; }
    .btn:disabled { opacity: .55; cursor: not-allowed; }
  `]
})
export class PrivacyPolicyPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly legalConsentService = inject(LegalConsentService);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isLoggedIn = signal(false);

  ngOnInit(): void {
    this.isLoggedIn.set(this.authService.getActiveAccount() !== null);
  }

  async acceptPrivacy(): Promise<void> {
    if (!this.isLoggedIn() || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.message.set('');

    try {
      await firstValueFrom(this.legalConsentService.acceptPrivacy());
      this.authService.invalidateLegalConsentCache();

      const pendingRoute = await this.authService.getPendingLegalRouteAsync(true);
      if (pendingRoute) {
        await this.router.navigate([pendingRoute], { replaceUrl: true });
        return;
      }

      const roles = await this.authService.getRolesAsync();
      const targetRoute = roles.includes('TeamMember') ? '/my-media' : '/home';
      await this.router.navigate([targetRoute], { replaceUrl: true });
    } catch {
      this.message.set('Unable to accept privacy policy right now. Please try again.');
    } finally {
      this.busy.set(false);
    }
  }
}
